"use client";

import * as React from "react";
import { createContext, useCallback, useRef, useEffect } from "react";
import {getPusherClient} from "@/lib/pusher/pusher-client";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { pusherGetUserChannelName } from "@/lib/pusher/pusher-tools";
import { Channel } from "pusher-js";

export interface ServerNotificationsProviderProps {
    children: React.ReactNode;
}

type EventCallback = (payload: any) => void;
type EventType = string;

type ServerNotificationsContextType = {
    /**
     * Subscribe to a specific event type on the user update channel
     * @param eventType - The type of event to listen for
     * @param callback - Function to call when the event is received
     * @returns Unsubscribe function
     */
    subscribe: (eventType: EventType, callback: EventCallback) => () => void;

    /**
     * Unsubscribe from a specific event type
     * @param eventType - The type of event to stop listening for
     * @param callback - The callback function to remove
     */
    unsubscribe: (eventType: EventType, callback: EventCallback) => void;

    /**
     * Check if currently connected to the user channel
     */
    isConnected: boolean;
};

export const ServerNotificationsContext = createContext<ServerNotificationsContextType>({
    subscribe: () => () => {},
    unsubscribe: () => {},
    isConnected: false,
});

export function ServerNotificationsProvider({ children }: ServerNotificationsProviderProps) {
    const currentUser = useSelector((state: RootState) => state.app.currentUser);

    // Use refs to persist across renders
    const eventListenersRef = useRef<Map<EventType, Set<EventCallback>>>(new Map());
    const channelRef = useRef<Channel | null>(null);
    const [isConnected, setIsConnected] = React.useState(false);

    // Subscribe to events on the pusher channel
    const subscribe = useCallback((eventType: EventType, callback: EventCallback) => {
        const listeners = eventListenersRef.current;

        if (!listeners.has(eventType)) {
            listeners.set(eventType, new Set());
        }

        listeners.get(eventType)!.add(callback);

        // If we have an active channel, bind the event
        if (channelRef.current) {
            channelRef.current.bind(eventType, callback);
        }

        // Return unsubscribe function
        return () => {
            unsubscribe(eventType, callback);
        };
    }, []);

    // Unsubscribe from events
    const unsubscribe = useCallback((eventType: EventType, callback: EventCallback) => {
        const listeners = eventListenersRef.current;
        const eventCallbacks = listeners.get(eventType);

        if (eventCallbacks) {
            eventCallbacks.delete(callback);

            // If no more listeners for this event type, remove the map entry
            if (eventCallbacks.size === 0) {
                listeners.delete(eventType);
            }

            // Unbind from pusher channel
            if (channelRef.current) {
                channelRef.current.unbind(eventType, callback);
            }
        }
    }, []);

    // Setup pusher channel subscription
    useEffect(() => {
        if (!currentUser) {
            // console.log('no user, will unsubscribe from channel');

            // Clean up if no user
            if (channelRef.current) {
                getPusherClient()?.unsubscribe(channelRef.current.name);
                channelRef.current = null;
                setIsConnected(false);
            }
            return;
        }

        const channelName = pusherGetUserChannelName(currentUser._id);

        // Unsubscribe from previous channel if it exists
        if (channelRef.current && channelRef.current.name !== channelName) {
            getPusherClient()?.unsubscribe(channelRef.current.name);
            channelRef.current = null;
        }

        // Subscribe to new channel
        if (!channelRef.current) {
            console.log("Subscribing to channel", channelName);
            const channel = getPusherClient()?.subscribe(channelName);
            channelRef.current = channel;

            // Bind all existing event listeners to the new channel
            const listeners = eventListenersRef.current;
            for (const [eventType, callbacks] of listeners.entries()) {
                for (const callback of callbacks) {
                    channel.bind(eventType, callback);
                }
            }

            // Handle connection state
            channel.bind('pusher:subscription_succeeded', () => {
                setIsConnected(true);
            });

            channel.bind('pusher:subscription_error', () => {
                setIsConnected(false);
            });
        }

        // Cleanup function
        return () => {
            if (channelRef.current) {
                getPusherClient()?.unsubscribe(channelRef.current.name);
                channelRef.current = null;
                setIsConnected(false);
            }
        };
    }, [currentUser?._id]);

    const contextValue: ServerNotificationsContextType = {
        subscribe,
        unsubscribe,
        isConnected,
    };

    return (
        <ServerNotificationsContext.Provider value={contextValue}>
            {children}
        </ServerNotificationsContext.Provider>
    );
}
